import React, { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { X, Megaphone, Download } from "lucide-react";
import LeadDialog, { DOCS } from "@/components/forms/LeadDialog";

const KEY = "bef_sponsor_proposal_popup";
const EXCLUDED = ["/register", "/privacy", "/terms", "/refund-policy"];

const readState = () => {
  try {
    return JSON.parse(localStorage.getItem(KEY)) || {};
  } catch {
    return {};
  }
};
const writeState = (s) => {
  try {
    localStorage.setItem(KEY, JSON.stringify(s));
  } catch {}
};

export default function SponsorProposalSlideIn() {
  const { pathname } = useLocation();
  const [show, setShow] = useState(false);
  const [captured, setCaptured] = useState(false);

  useEffect(() => {
    if (EXCLUDED.includes(pathname)) return;
    const s = readState();
    if (s.captured) return;
    if (s.dismissedAt && Date.now() - s.dismissedAt < 3 * 86400000) return;

    let done = false;
    const reveal = () => {
      if (done) return;
      done = true;
      setShow(true);
      cleanup();
    };
    const onScroll = () => {
      const d = document.documentElement;
      const max = d.scrollHeight - d.clientHeight;
      if (max > 0 && d.scrollTop / max > 0.3) reveal();
    };
    const timer = setTimeout(reveal, 12000);
    const cleanup = () => {
      clearTimeout(timer);
      window.removeEventListener("scroll", onScroll);
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    return cleanup;
  }, [pathname]);

  const dismiss = () => {
    writeState({ dismissedAt: Date.now() });
    setShow(false);
  };
  const markCaptured = () => {
    writeState({ captured: true });
    setCaptured(true);
  };
  const onDialogClose = () => {
    if (captured || readState().captured) setShow(false);
  };

  return (
    <AnimatePresence>
      {show && !EXCLUDED.includes(pathname) && (
        <motion.aside
          initial={{ x: 420, opacity: 0 }}
          animate={{ x: 0, opacity: 1 }}
          exit={{ x: 420, opacity: 0 }}
          transition={{ type: "spring", stiffness: 260, damping: 28 }}
          className="fixed bottom-24 right-5 z-40 w-[320px] overflow-hidden rounded-2xl border border-lime/30 bg-navy shadow-[0_25px_60px_rgba(0,0,0,0.45)]"
          data-testid="sponsor-slidein"
        >
          <div className="pointer-events-none absolute -right-10 -top-10 h-36 w-36 rounded-full bg-lime/20 blur-2xl" />
          <button
            onClick={dismiss}
            aria-label="Dismiss"
            data-testid="sponsor-slidein-close"
            className="absolute right-3 top-3 z-10 flex h-7 w-7 items-center justify-center rounded-full bg-white/10 text-white/70 transition-colors hover:bg-white/20 hover:text-white"
          >
            <X className="h-4 w-4" />
          </button>
          <div className="relative p-5">
            <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-lime/15 text-lime">
              <Megaphone className="h-5 w-5" />
            </span>
            <p className="mt-3 text-[11px] font-bold uppercase tracking-[0.18em] text-lime">Limited sponsorship slots</p>
            <h3 className="mt-1.5 font-display text-lg font-bold leading-snug text-white">
              Position your brand in front of 800+ ethanol decision-makers.
            </h3>
            <p className="mt-2 text-sm leading-relaxed text-slate-300">
              Tiers, branding deliverables and stage visibility — get the official 2026 Sponsorship Proposal.
            </p>
            <LeadDialog
              formType="sponsorship_proposal"
              title="Get the Sponsorship Proposal"
              description="Tiers, deliverables and branding opportunities — instant PDF download."
              ctaLabel="Get the proposal"
              successMsg="Sent. Your download is starting now."
              downloadUrl={DOCS.sponsorProposal.url}
              downloadName={DOCS.sponsorProposal.name}
              onSuccess={markCaptured}
              onClose={onDialogClose}
              trigger={
                <button
                  data-testid="sponsor-slidein-cta"
                  className="mt-4 inline-flex w-full items-center justify-center gap-2 rounded-full bg-lime px-5 py-3 text-sm font-bold text-navy transition-all hover:-translate-y-0.5 hover:shadow-[0_10px_30px_rgba(194,240,60,0.4)]"
                >
                  <Download className="h-4 w-4" /> Get the Sponsorship Proposal
                </button>
              }
            />
            <p className="mt-2.5 text-center text-[11px] text-white/40">Instant PDF download · No spam</p>
          </div>
        </motion.aside>
      )}
    </AnimatePresence>
  );
}
